/**
 * Modules dependencies.
 */

import { Carousel, Image, Slide, VideoPlayer } from 'src/components/core';
import { NewsExhibitionsProps } from 'src/types/news-exhibitions';
import { ReactElement, useMemo, useRef, useState } from 'react';
import { media } from '@untile/react-components';
import head from 'lodash/head';
import map from 'lodash/map';
import size from 'lodash/size';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = NewsExhibitionsProps & {
  className?: string;
};

/**
 * `CarouselItem` type.
 */

type CarouselVideoItem = {
  coverImage: string;
  type: 'video';
  url: string;
};

type CarouselImageItem = {
  image: string;
  type: 'image';
};

type CarouselItem = CarouselImageItem | CarouselVideoItem;

/**
 * `SlideItem` styled component.
 */

const SlideItem = styled.div`
  cursor: pointer;
  height: 0;
  overflow: hidden;
  padding-bottom: 61%;
  position: relative;

  ${media.min('md')`
    padding-bottom: 56%;
  `}
`;

/**
 * Carousel config.
 */

const carouselConfig = {
  breakpoints: {
    768: {
      slidesPerView: 1.1,
      spaceBetween: 30
    }
  }
};

/**
 * `NewsExhibitionsCarousel` component.
 */

const NewsExhibitionsCarousel = (props: Props): ReactElement => {
  const { featuredImages, videoImage, videoUrl } = props;
  const gallery = useMemo<CarouselItem[]>(() => {
    return [
      videoImage &&
        videoUrl && {
          coverImage: videoImage,
          type: 'video',
          url: videoUrl
        },
      ...map(featuredImages, image => ({
        image,
        type: 'image' as const
      }))
    ].filter(Boolean) as CarouselItem[];
  }, [featuredImages, videoImage, videoUrl]);

  const firstItem = useMemo(() => head(gallery), [gallery]);

  const sliderRef = useRef<any>();
  const [activeSlide, setActiveSlide] = useState<number>(0);

  return size(gallery) > 1 ? (
    <Carousel
      activeSlide={activeSlide}
      autoplay={{
        delay: 10000,
        disableOnInteraction: true
      }}
      carouselConfig={carouselConfig}
      containerModifierClass={'news-exhibitions'}
      onSetActiveSlide={setActiveSlide}
      ref={sliderRef}
      totalItems={size(gallery)}
    >
      <>
        {map(gallery, (item, index) => (
          <Slide key={index}>
            {item.type === 'image' && (
              <SlideItem>
                <Image
                  alt={item.image}
                  fill
                  src={item.image}
                  style={{ objectFit: 'cover' }}
                />
              </SlideItem>
            )}

            {item.type === 'video' && (
              <VideoPlayer
                image={item.coverImage}
                playing={activeSlide === index}
                url={item.url}
              />
            )}
          </Slide>
        ))}
      </>
    </Carousel>
  ) : (
    <>
      {firstItem.type === 'image' && (
        <SlideItem>
          <Image
            alt={firstItem.image}
            fill
            src={firstItem.image}
            style={{ objectFit: 'cover' }}
          />
        </SlideItem>
      )}

      {firstItem.type === 'video' && (
        <VideoPlayer
          image={firstItem.coverImage}
          playing
          url={firstItem.url}
        />
      )}
    </>
  );
};

/**
 * Export `NewsExhibitionsCarousel` component.
 */

export default NewsExhibitionsCarousel;
